import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const REASON_LABEL = {
  user_signed_out: "Signed out",
  idle_timeout: "Idle timeout",
  refresh_token_expired: "Session expired",
  refresh_token_invalid: "Invalid session",
  account_deactivated: "Account deactivated",
  user_not_found: "User removed",
};

function PolicyField({ label, hint, value, onChange, onSave, testId }) {
  return (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      <div className="flex gap-2">
        <Input type="number" min="0" value={value} onChange={(e) => onChange(e.target.value)} data-testid={`${testId}-input`} className="max-w-[140px]" />
        <Button size="sm" onClick={onSave} className="bg-[#255058] text-white hover:bg-[#1c3d43]" data-testid={`${testId}-save`}>Save</Button>
      </div>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

export function AttendancePolicies() {
  const [idle, setIdle] = useState("0");
  const [escalation, setEscalation] = useState("4");
  const [events, setEvents] = useState([]);
  const [greetings, setGreetings] = useState({});

  useEffect(() => {
    api.get("/hrms/settings/greetings").then(({ data }) => setGreetings(data.library)).catch(() => {});
    api.get("/auth/session-policy").then(({ data }) => setIdle(String(data.idle_timeout_minutes))).catch(() => {});
    api.get("/hrms/settings/escalation").then(({ data }) => setEscalation(String(data.escalation_hours))).catch(() => {});
    api.get("/auth/session-events").then(({ data }) => setEvents(data.items || [])).catch(() => {});
  }, []);

  const save = async (fn, message) => {
    try {
      await fn();
      toast.success(message);
    } catch (error) {
      toast.error(apiErr(error));
    }
  };

  return (
    <Card className="p-5" data-testid="hrms-attendance-policies">
      <div className="font-display text-lg font-semibold">Session & approval policies</div>
      <div className="mt-4 grid gap-6 md:grid-cols-2">
        <PolicyField
          label="Idle sign-out (minutes)"
          hint="0 = never sign out for inactivity. Clocked-in employees are never signed out during a shift; a warning appears 2 minutes before."
          value={idle}
          onChange={setIdle}
          testId="policy-idle-timeout"
          onSave={() => save(() => api.put("/auth/session-policy", { idle_timeout_minutes: Number(idle) }), "Idle policy saved")}
        />
        <PolicyField
          label="Escalate pending requests after (hours)"
          hint="Unanswered leave, early logout, overtime and regularization requests move to the approver's superior, then Super Admin."
          value={escalation}
          onChange={setEscalation}
          testId="policy-escalation"
          onSave={() => save(() => api.put("/hrms/settings/escalation", { escalation_hours: Number(escalation) }), "Escalation timing saved")}
        />
      </div>
      <div className="mt-6 text-sm font-semibold">Greeting messages</div>
      <p className="text-xs text-muted-foreground">One message per line; a random one is shown. Placeholders: {"{name} {daypart} {shift} {end} {left} {used} {remaining} {worked} {break}"}</p>
      <div className="mt-2 grid gap-3 md:grid-cols-2" data-testid="greetings-editor">
        {Object.keys(greetings).map((key) => (
          <div key={key}>
            <Label className="capitalize">{key}</Label>
            <Textarea
              rows={3}
              value={(greetings[key] || []).join("\n")}
              onChange={(e) => setGreetings({ ...greetings, [key]: e.target.value.split("\n") })}
              data-testid={`greetings-${key}`}
            />
          </div>
        ))}
      </div>
      <Button size="sm" className="mt-2 bg-[#255058] text-white hover:bg-[#1c3d43]" data-testid="greetings-save"
        onClick={() => save(() => api.put("/hrms/settings/greetings", { library: greetings }), "Greetings saved")}>
        Save greetings
      </Button>
      <div className="mt-6 text-sm font-semibold">Sign-out log</div>
      <div className="mt-2 max-h-64 divide-y divide-border overflow-y-auto rounded-md border border-border" data-testid="session-events-log">
        {events.map((item) => (
          <div key={item.id} className="flex justify-between gap-4 px-3 py-2 text-xs">
            <span>
              <span className="font-medium">{item.user_name || item.email || "Unknown user"}</span> · {REASON_LABEL[item.reason] || item.reason.replace(/_/g, " ")}
            </span>
            <span className="shrink-0 text-muted-foreground">{new Date(item.created_at).toLocaleString()}</span>
          </div>
        ))}
        {!events.length && <div className="px-3 py-6 text-center text-xs text-muted-foreground">No sign-out events recorded yet.</div>}
      </div>
    </Card>
  );
}
